import type { Workspace } from '../types/workspace'
import type { ChecklistItem } from '../types/schema'
import type { BudgetCategory } from '../types/budget'
import { DEFAULT_BUDGET_CATEGORY, normalizeBudgetCategory } from '../types/budget'
import type { StockItem, StockRequirement } from '../types/stock'
import type { Quota } from '../types/quota'
import type { ResourceImpactPreview, ResourceReadiness, ResourceRiskItem, ResourceSignal } from '../types/resource'
import { isBudgetEnabled, isQuotaEnabled, isStockEnabled } from './featureRegistry'

type ResourceNode = Partial<ChecklistItem> & {
  id: string
  title?: string
  requiredStockIds?: string[]
  stockRequirements?: StockRequirement[]
}

type ResourceElement = {
  id: string
  title?: string
}

const clampQuantity = (value: unknown): number => {
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return 1
  return Math.max(1, Math.floor(numeric))
}

export const normalizeStockRequirements = (node: Partial<ResourceNode> | null | undefined): StockRequirement[] => {
  const explicit = Array.isArray(node?.stockRequirements)
    ? node.stockRequirements
      .map((entry) => ({
        stockItemId: String(entry.stockItemId ?? '').trim(),
        quantity: clampQuantity(entry.quantity),
        unitOverride: typeof entry.unitOverride === 'string' && entry.unitOverride.trim() ? entry.unitOverride.trim() : null,
        required: entry.required !== false,
      }))
      .filter((entry) => entry.stockItemId)
    : []
  if (explicit.length > 0) return explicit
  return Array.isArray(node?.requiredStockIds)
    ? node.requiredStockIds
      .map((stockItemId) => ({ stockItemId: String(stockItemId).trim(), quantity: 1, unitOverride: null, required: true }))
      .filter((entry) => entry.stockItemId)
    : []
}

export const stockRequirementIds = (requirements: StockRequirement[]): string[] =>
  Array.from(new Set(requirements.map((entry) => entry.stockItemId).filter(Boolean)))

const severityRank = (severity: ResourceSignal['severity']) =>
  severity === 'blocked' ? 2 : severity === 'warning' ? 1 : 0

const readinessStatus = (signals: ResourceSignal[]): ResourceReadiness['status'] => {
  const max = signals.reduce((rank, signal) => Math.max(rank, severityRank(signal.severity)), 0)
  return max === 2 ? 'blocked' : max === 1 ? 'warning' : 'clear'
}

const formatMinor = (minor: number, currency: string) => `${currency} ${(minor / 100).toFixed(2)}`

const finiteNumberOr = (value: unknown, fallback: number): number => {
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric : fallback
}

export const quotaWeightedContribution = (node: Partial<ChecklistItem> | null | undefined): number => {
  const weightage = Math.max(1, finiteNumberOr(node?.weightage, 1))
  const urgency = Math.max(1, Math.min(5, finiteNumberOr(node?.urgencyStars, 3)))
  const complexity = Math.max(1, Math.min(5, finiteNumberOr(node?.complexityStars, 3)))
  return Math.max(1, Math.round(weightage * (1 + (urgency / 5) * 0.7 + (complexity / 5) * 0.3)))
}

const quotaImpactLabel = (contribution: number): string => {
  if (contribution >= 8) return 'High quota impact'
  if (contribution >= 4) return 'Medium quota impact'
  return 'Low quota impact'
}

export const buildResourceSignals = (input: {
  workspace: Workspace | null | undefined
  node: ResourceNode
  stockItems?: StockItem[]
  quotas?: Quota[]
  currency?: string
  completionStage?: boolean
}): ResourceReadiness => {
  const { workspace, node, stockItems = [], quotas = [], completionStage = false } = input
  if (!workspace || (node.workspaceId && node.workspaceId !== workspace.id)) {
    return { status: 'clear', signals: [] }
  }
  const signals: ResourceSignal[] = []
  const currency = input.currency ?? workspace?.settings?.currency ?? 'USD'

  if (isBudgetEnabled(workspace)) {
    const actualSpend = Number(node.actualSpend ?? 0)
    const estimatedCost = Number(node.estimatedCost ?? 0)
    const budgetLimit = Number(node.budgetLimit ?? 0)
    if (budgetLimit > 0) {
      const projected = actualSpend + Math.max(0, estimatedCost)
      const ratio = projected / budgetLimit
      if (ratio > 1) {
        signals.push({
          kind: 'budget',
          label: 'Budget risk',
          severity: completionStage ? 'blocked' : 'warning',
          summary: `Projected ${formatMinor(projected, currency)} over ${formatMinor(budgetLimit, currency)}`,
        })
      } else if (ratio >= 0.8) {
        signals.push({
          kind: 'budget',
          label: 'Budget risk',
          severity: 'warning',
          summary: `${Math.round(ratio * 100)}% of budget committed`,
        })
      }
    }
  }

  if (isStockEnabled(workspace)) {
    const stockById = new Map(stockItems.map((item) => [item.id, item]))
    const requirements = normalizeStockRequirements(node)
    type Shortage = { requirement: StockRequirement; item: StockItem | null; available: number }
    const shortages: Shortage[] = requirements.flatMap((requirement): Shortage[] => {
      const item = stockById.get(requirement.stockItemId)
      if (!item) return [{ requirement, item: null, available: 0 }]
      const available = Math.max(0, item.quantity - item.reservedQuantity)
      return available < requirement.quantity ? [{ requirement, item, available }] : []
    })
    const maintenanceCount = requirements
      .map((requirement) => stockById.get(requirement.stockItemId) ?? null)
      .filter((item) => item?.status === 'Maintenance')
      .length

    if (shortages.length > 0) {
      const blocking = completionStage && shortages.some((entry) => entry.requirement.required !== false)
      signals.push({
        kind: 'stock',
        label: 'Stock short',
        severity: blocking ? 'blocked' : 'warning',
        summary: `${shortages.length} requirement${shortages.length === 1 ? '' : 's'} short`,
        detail: shortages.map(({ item, requirement, available }) =>
          `${item?.name ?? requirement.stockItemId}: ${available}/${requirement.quantity}`,
        ).join(', '),
      })
    } else if (maintenanceCount > 0) {
      signals.push({
        kind: 'stock',
        label: 'Stock short',
        severity: 'warning',
        summary: `${maintenanceCount} required item${maintenanceCount === 1 ? '' : 's'} under maintenance`,
      })
    }
  }

  if (isQuotaEnabled(workspace)) {
    const contribution = quotaWeightedContribution(node)
    const activeWeighted = quotas.some((quota) => quota.targets.weighted_progress)
    if (activeWeighted && contribution > 0) {
      signals.push({
        kind: 'quota',
        label: 'Quota impact',
        severity: 'clear',
        summary: `+${contribution} weighted progress`,
      })
    }
  }

  return { status: readinessStatus(signals), signals }
}

export const buildResourceImpactPreview = (
  node: ResourceNode,
  category: BudgetCategory = DEFAULT_BUDGET_CATEGORY,
): ResourceImpactPreview => {
  const quotaContribution = quotaWeightedContribution(node)
  return {
    budgetCategory: normalizeBudgetCategory(category),
    stockRequirements: normalizeStockRequirements(node),
    quotaContribution,
    quotaImpactLabel: quotaImpactLabel(quotaContribution),
  }
}

export const buildResourceRiskQueue = (input: {
  workspace: Workspace | null | undefined
  checklistItems: ChecklistItem[]
  elements: ResourceElement[]
  stockItems?: StockItem[]
  quotas?: Quota[]
}): ResourceRiskItem[] => {
  const elementById = new Map(input.elements.map((element) => [element.id, element]))
  const risks: ResourceRiskItem[] = []
  for (const item of input.checklistItems) {
    const readiness = buildResourceSignals({
      workspace: input.workspace,
      node: item,
      stockItems: input.stockItems,
      quotas: input.quotas,
    })
    const visibleSignals = readiness.signals.filter((signal) => signal.severity !== 'clear')
    if (visibleSignals.length === 0) continue
    risks.push({
      id: item.id,
      title: item.title,
      ownerTitle: elementById.get(item.parentContainerId)?.title,
      signals: visibleSignals,
    })
  }
  return risks.sort((first, second) => {
    const severity = Math.max(...second.signals.map((signal) => severityRank(signal.severity)))
      - Math.max(...first.signals.map((signal) => severityRank(signal.severity)))
    return severity || first.title.localeCompare(second.title)
  })
}
